import React, { useEffect, useState } from "react";
import { Exercise } from "../../models/Exercise";
import useCalendarSale from "../ThreeStep/hooks/useCalendarSale";
import { useSelector } from "react-redux";
import LoadingSpinner from "../../../features/Loading/LoadingSpinner";
import { useTranslation } from "react-i18next";
import { State } from "../../store";
import EAButton from "../shared/Buttons/EAButton";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import usePreBasket from "../Universal/hooks/usePreBasket";
import useBasketManager from "../FinalizePage/hooks/useBasketManager";
import TicketLineSummary from "../Universal/TicketLineSummary";
import PreBasketSummary from "../Universal/PreBasketSummary";
import useCalculatePreBasketQnt from "../Universal/hooks/useCalculatePreBasketQnt";
import { format } from "date-fns";
import { FaMapPin, FaRegClock } from "react-icons/fa";
import useTourLimits from "./hooks/useTourLimits";
import getId from "../../../utils/getId";
import EASkeletonLoader from "../shared/EASkeletonLoader";
import EAAlert from "../shared/EAAlert";
import EAChip from "../shared/Chip/EAChip";

interface IProps {
  item: Exercise;
}

const SingleConcert: React.FC<IProps> = ({ item }: IProps) => {
  const { t } = useTranslation();
  const { getTicketsByExercise, tickets, ticketAccess } = useCalendarSale();
  const [open, setOpen] = useState(false);
  const { preBasket, addItemToBasket, removeItemFromBasket, clearBasket, setItemQuantity } = usePreBasket();
  const { getBasket } = useBasketManager();
  const showTicketLimits = useSelector((state: State) => state.config.configuration.showTicketsPoolForEventsSale);
  const { qntState } = useCalculatePreBasketQnt(preBasket);
  const { limits, isLoading, isFallback, getLimitForAssort, fetchLimits } = useTourLimits();
  const lowestPrice = tickets.reduce((r, b) => Math.min(r, b.price), Number.POSITIVE_INFINITY);
  const [city, ...rest] = item.location.split(" ");
  const location = rest.join(" ");
  const availableCapacity = item.capacity - item.reserved;
  const isSoldOut = availableCapacity <= 0;

  const handleDeleteBasket = () => {
    clearBasket();
    setTimeout(() => {
      getBasket();
    }, 200);
  };

  // Oblicz sumę wszystkich biletów w prebasket dla tego konkretnego koncertu
  const totalTicketsInBasket =
    qntState && qntState.length > 0 && ticketAccess?.exerciseIds
      ? qntState
          .filter((item) => item.exerciseIds === ticketAccess.exerciseIds)
          .reduce((acc: number, item) => acc + item.count, 0)
      : preBasket.reduce((acc: number, item) => acc + item.qnt, 0);

  // Oblicz globalny limit - z nowego API lub fallback na capacity
  // maxQuantity === 0 lub null -> brak limitu (opieramy się wtedy na capacity)
  // Uwaga: bierzemy pod uwagę TYLKO te limity, które odpowiadają biletom widocznym w `tickets`
  const visibleAssortIds = new Set(tickets.map((ticket) => getId(ticket)));

  const hardLimited = limits.filter((limit) => {
    const limitAssortId = getId(limit.assortIds);
    const isVisibleAssort = visibleAssortIds.has(limitAssortId);

    return isVisibleAssort && limit.maxQuantity !== null && limit.maxQuantity !== 0;
  });

  // Sumujemy tylko remainingQuantity dla pozycji z limitem
  const limitedRemainingTotal = hardLimited.reduce((sum, limit) => {
    if (limit.remainingQuantity === null || limit.remainingQuantity === undefined) {
      return sum;
    }
    return sum + limit.remainingQuantity;
  }, 0);

  const hasHardLimits = hardLimited.length > 0;

  // Jeśli suma limitów jest większa niż realnie dostępna pojemność, ufamy pojemności wydarzenia
  const shouldFallbackToCapacity = !isFallback && hasHardLimits && limitedRemainingTotal > availableCapacity;

  const globalLimit = isFallback
    ? availableCapacity
    : (() => {
        // Brak twardych limitów lub wykryta niespójność -> opieramy się na capacity
        if (!hasHardLimits || shouldFallbackToCapacity) {
          return availableCapacity;
        }

        // W przeciwnym razie korzystamy z sumy remainingQuantity, ale nigdy nie przekraczamy pojemności
        return Math.min(availableCapacity, limitedRemainingTotal);
      })();

  const isCapacityExceeded = totalTicketsInBasket >= globalLimit;
  useEffect(() => {
    getTicketsByExercise(item.exerciseGroup, item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limity pobieramy dopiero po rozwinięciu listy biletów
  useEffect(() => {
    if (!open) return;
    const exerciseIds = `${item.objectDb},${item.objectId}`;
    fetchLimits(exerciseIds);
  }, [open, item.objectDb, item.objectId, fetchLimits]);

  const IconWithInfo = ({
    icon,
    primaryInfo,
    secondaryInfo,
  }: {
    icon: React.ReactNode;
    primaryInfo: string;
    secondaryInfo: string;
  }) => {
    return (
      <div className="single-concert__icon-with-info">
        {icon}
        <div className="single-concert__icon-with-info-content">
          <h4>{primaryInfo}</h4>
          <p className="text-body-small">{secondaryInfo}</p>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`single-concert flex-between-center border${
          isSoldOut ? " single-concert--soldout" : ""
        }`}
      >
        <div className="single-concert__left-container">
          <IconWithInfo
            icon={<FaRegClock className="single-icon" />}
            primaryInfo={format(item.startTime, "dd.MM.yyyy")}
            secondaryInfo={format(item.startTime, "HH:mm")}
          />
          <IconWithInfo icon={<FaMapPin className="single-icon" />} primaryInfo={city} secondaryInfo={location} />
        </div>

          <div className="single-concert__right-container">
          <div className="single-concert__price">
            {isSoldOut && <EAChip label="Wyprzedane" variant="dark" />}
            <h5>
              {lowestPrice !== Infinity ? (
                <>
                  {t("ceny_od")} {lowestPrice} {t("zl")}
                </>
              ) : (
                <LoadingSpinner />
              )}
            </h5>
          </div>

          <div className="single-concert__button">
            <div className="capacity-procent">
              {(() => {
                const totalRemaining = item.capacity - item.reserved;

                // Brak miejsc lub wyprzedane
                if (totalRemaining <= 0) {
                  return <div className="status low">Wyprzedane / brak miejsc</div>;
                }

                const remaining = totalRemaining / item.capacity; // ile % zostało wolne

                if (remaining >= 0.9) {
                  return <div className="status high">Duża dostępność</div>;
                } else if (remaining > 0.5) {
                  return <div className="status mediums">Średnia dostępność</div>;
                } else {
                  return <div className="status low">Mała dostępność</div>;
                }
              })()}
            </div>
            <EAButton
              title={!open ? t("pokaz_bilety") : t("schowaj_bilety")}
              onClick={() => setOpen(!open)}
              endIcon={open ? <IoChevronUp /> : <IoChevronDown />}
              variant={!open ? "filled" : "outlined"}
              fullWidth
            />{" "}
          </div>
        </div>
      </div>

      {open && (
        <div className="single-event-tickets border">
          <div className="head-title flex-between-center">
            <b className="head-title__label">{t("wybierz_bilety")}</b>
            {showTicketLimits && (
              <span className="head-title__limit">
                {t("pozostalo")}:{" "}
                <b>
                  {globalLimit} {t("biletow")}
                </b>
              </span>
            )}
          </div>
          {isLoading ? (
            <div className="tickets-skeleton-container">
              {tickets.map((_, index) => (
                <EASkeletonLoader key={index} height="60px" className="ticket-skeleton" />
              ))}
            </div>
          ) : globalLimit > 0 ? (
            <div className="tickets-list-group">
              {tickets.map((itemTicket, index) => {
                const assortLimit = getLimitForAssort(
                  getId(itemTicket),
                  `${item.exerciseGroup.objectDb},${item.exerciseGroup.objectId}`,
                );
                const isUnavailable = assortLimit === 0;

                return (
                  <div className={`ticket-block${isUnavailable ? " ticket-block--unavailable" : ""}`} key={index}>
                    <TicketLineSummary
                      item={itemTicket}
                      exercise={ticketAccess}
                      preBasket={preBasket}
                      addItemToBasket={addItemToBasket}
                      removeItemFromBasket={removeItemFromBasket}
                      setItemQuantity={setItemQuantity}
                      qntState={qntState}
                      allowManualInput={true}
                      // Globalny limit (capacity - reserved lub globalLimit z nowych API)
                      eventCapacityLimit={globalLimit}
                      // Limit per bilet – jeśli jest mniejszy niż globalny, to on zadecyduje;
                      // jeśli większy, zostanie przycięty przez eventCapacityLimit.
                      assortSpecificLimit={assortLimit}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <EAAlert message={t("wszystkie_bilety_zostaly_sprzedane")} type="error" />
          )}

          {globalLimit > 0 && isCapacityExceeded && (
            <div className="alert alert-yellow">
              {t("osiagnieto_maksymalna_liczbe_biletow_dostepnych_dla_tego_wydarzenia")}
            </div>
          )}

          <PreBasketSummary preBasket={preBasket} clearBasket={handleDeleteBasket} eventDate={item.startTime} />
        </div>
      )}
    </>
  );
};;;

export default SingleConcert;
